import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Wifi, WifiOff } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';

export function ApiConnectionStatus() {
  const [isOnline, setIsOnline] = useState(true);
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const health = await apiClient.checkHealth();
        const newOnlineStatus = health.online;
        
        if (isOnline !== newOnlineStatus) {
          setIsOnline(newOnlineStatus);
          setShowAlert(!newOnlineStatus); // Show alert when going offline
        }
      } catch (error: any) {
        // More detailed error handling for better user feedback
        const isConnectionError = error?.message?.includes('Network error') || 
                                  error?.code === 'ECONNREFUSED' ||
                                  error?.code === 'ENOTFOUND' ||
                                  error?.code === 'ETIMEDOUT';
        
        const is502Error = error?.response?.status === 502;
        const is503Error = error?.response?.status === 503;
        
        // Only show alerts for actual connection issues, not temporary 502/503 errors
        if (isConnectionError || is502Error || is503Error) {
          if (isOnline) {
            console.warn(`API health check failed (${error?.response?.status || error?.code}):`, error?.message || error);
            setIsOnline(false);
            setShowAlert(true);
          }
        } else {
          // For other errors (like auth errors), don't mark as offline
          console.debug('API health check failed with non-connection error:', error);
        }
      }
    };

    // Initial check with delay to allow backend to start
    const initialTimeout = setTimeout(checkConnection, 2000);

    // Check every 30 seconds
    const interval = setInterval(checkConnection, 30000);

    // Cleanup
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [isOnline]);

  // Auto-hide alert after 10 seconds when online
  useEffect(() => {
    if (isOnline && showAlert) {
      const timeout = setTimeout(() => setShowAlert(false), 10000);
      return () => clearTimeout(timeout);
    }
  }, [isOnline, showAlert]);

  if (!showAlert) return null;

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      <Alert variant={isOnline ? "default" : "destructive"}>
        {isOnline ? (
          <Wifi className="h-4 w-4" />
        ) : (
          <WifiOff className="h-4 w-4" />
        )}
        <AlertDescription>
          {isOnline ? (
            "✅ Connected to backend API"
          ) : (
            <>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                <span>Backend API temporarily unavailable</span>
              </div>
              <div className="text-xs mt-1 opacity-75">
                This usually resolves automatically. If it persists, the backend may be restarting or under maintenance.
              </div>
            </>
          )}
        </AlertDescription>
      </Alert>
    </div>
  );
}

export default ApiConnectionStatus;
